import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageCircle, CalendarCheck, Rocket, ChevronDown } from "lucide-react";
import { Reveal, SectionTag, Polaroid, StarDoodle } from "@/components/doodles";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const GOALS = ["Just starting out", "Lose weight & build habits", "Level up my performance", "Rebuild after injury"];

const STEPS = [
  { icon: MessageCircle, title: "You send this form", copy: "Thirty seconds, no commitment." },
  { icon: CalendarCheck, title: "I reply within 24 hrs", copy: "Usually way faster. I get excited." },
  { icon: Rocket, title: "Free intro call", copy: "We map your starting line — no pressure, no push." },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", goal: GOALS[0], message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Got it! Your message landed in my inbox — check yours for a little thank-you note.");
      setForm({ name: "", email: "", goal: GOALS[0], message: "" });
    } catch {
      toast.error("Hmm, that didn't send. Give it another try in a minute?");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-offwhite border-t-[3px] border-ink">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <Reveal><SectionTag className="text-pink">CONTACT</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3 leading-[0.95]">
              READY TO <span className="hl-lime">EVOLVE</span>?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-hand text-2xl sm:text-3xl text-pink mt-3 -rotate-1">seriously — I'm so glad you're here</p>
          </Reveal>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <form onSubmit={onSubmit} className="card-pop bg-white p-6 sm:p-10" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="font-bebas tracking-[0.2em] text-sm block mb-2">
                    YOUR NAME ★
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-input-name"
                    className="input-pop"
                    placeholder="Jordan Athlete"
                    value={form.name}
                    onChange={update("name")}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="font-bebas tracking-[0.2em] text-sm block mb-2">
                    YOUR EMAIL ★
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-input-email"
                    type="email"
                    className="input-pop"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="contact-goal" className="font-bebas tracking-[0.2em] text-sm block mb-2">
                  WHERE ARE YOU AT?
                </label>
                <div className="relative">
                  <select id="contact-goal" data-testid="contact-input-goal" className="input-pop appearance-none pr-10" value={form.goal} onChange={update("goal")}>
                    {GOALS.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="contact-message" className="font-bebas tracking-[0.2em] text-sm block mb-2">
                  TELL ME YOUR STORY (OPTIONAL)
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  className="input-pop min-h-[120px] resize-y"
                  placeholder="Where are you now, where do you want to be, what's tripping you up?"
                  value={form.message}
                  onChange={update("message")}
                />
              </div>
              <button type="submit" data-testid="contact-form-submit" disabled={sending} className="btn-pop bg-lime text-ink w-full px-8 py-4 mt-7 text-lg disabled:opacity-60 disabled:cursor-wait">
                {sending ? "SENDING..." : "SEND IT — LET'S EVOLVE"} {!sending && <Send className="w-5 h-5" />}
              </button>
              <p className="font-hand text-xl text-ink/50 text-center mt-4">no spam, no pressure — just a coach who replies</p>
            </form>
          </Reveal>

          <div className="space-y-8">
            <Reveal delay={0.08}>
              <div className="card-pop bg-ink text-cream p-8 sm:p-10 rotate-1" data-testid="contact-steps">
                <p className="font-bebas tracking-[0.25em] text-lg text-gold">WHAT HAPPENS NEXT</p>
                <ul className="mt-6 space-y-6">
                  {STEPS.map((s, i) => (
                    <li key={s.title} className="flex items-start gap-4">
                      <span className="w-11 h-11 shrink-0 bg-cream text-ink border-[3px] border-cream flex items-center justify-center shadow-[3px_3px_0_rgba(249,248,246,0.5)]">
                        <s.icon className="w-5 h-5" />
                      </span>
                      <div>
                        <p className="font-display font-extrabold uppercase text-lg">
                          <span className="font-hand text-pink text-2xl mr-2">{i + 1}.</span>
                          {s.title}
                        </p>
                        <p className="text-sm font-semibold text-cream/65 mt-1">{s.copy}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex items-center gap-2">
                  <StarDoodle className="w-5 h-5 text-gold" />
                  <p className="font-hand text-2xl text-electric -rotate-1">
                    you don't have to know where to start. that's my job.
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="max-w-xs mx-auto lg:ml-auto">
                <Polaroid
                  eager
                  src="/img/crossfit-comp.webp"
                  alt="Rosa competing at a CrossFit competition"
                  caption="your coach, hype squad & biggest fan"
                  rotate="rotate-3"
                  className="w-full"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
